import * as Sentry from '@sentry/react';
import { useEffect, useState, lazy, Suspense } from 'react';
import { Switch, Route, BrowserRouter as Router, useParams } from 'react-router-dom';
import { createBrowserHistory } from 'history';

import Navbar from './Navbar';
import LoadingPage from './LoadingPage';
import analytics from './analytics';
import { applyTemplateOverrides } from './templateOverrides';

Sentry.init({
  dsn: 'https://80fb23a41f8b4633997749e7c651843a@o394735.ingest.sentry.io/5646637',
});

const Builder = lazy(() => import('./Builder'));
const Sandbox = lazy(() => import('./Sandbox'));

let loading = false;

function LoadBuilder({ viewOnly }) {
  console.log('Loading existing builder', { viewOnly });
  const { id } = useParams();
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      if (loaded || loading) {
        return;
      }
      loading = true;
      try {
        const currentSearchParams = new URLSearchParams(window.location.search);
        const apiUrl = new URL(`https://quickchart.io/chart/render/${id}`);

        for (const [key, value] of currentSearchParams.entries()) {
          apiUrl.searchParams.append(key, value);
        }
        apiUrl.searchParams.append('format', 'raw');

        const resp = await fetch(apiUrl.toString());
        const data = await resp.json();
        if (typeof data.chart === 'string') {
          try {
            // Try parsing as JSON first
            data.chart = JSON.parse(data.chart);
          } catch (e) {
            // If it's not JSON, evaluate as JavaScript
            data.chart = eval(`(${data.chart})`);
          }
        }

        if (data.templateOverrides) {
          applyTemplateOverrides(data.chart, data.templateOverrides, false /* isVerison3 */);
        }

        let title =
          data.chart.options?.title?.text ||
          data.chart.options?.plugins?.title?.text ||
          data.chart.data?.datasets?.[0]?.label ||
          'Chart';
        if (!viewOnly) {
          title = `Edit: ${title}`;
        }
        if (title.length > 20) {
          title = title.substr(0, 20) + '...';
        }
        document.title = title;
        setConfig(data);
      } catch (err) {
        setError('Chart not found. Please check the URL and try again.');
      }
      setLoaded(true);
    })();
  });

  if (error) {
    return <LoadingPage hideSpinner text={error} />;
  }

  if (loaded) {
    return (
      <Suspense fallback={<LoadingPage text="Loading chart maker..." />}>
        <Builder baseConfig={config} viewOnly={viewOnly} />
      </Suspense>
    );
  }

  return <LoadingPage text="Fetching chart config..." />;
}

const Entrypoint = () => {
  const [sandboxConfig, setSandboxConfig] = useState(null);
  function handleSwitchToChartBuilder(config) {
    setSandboxConfig(config);
  }

  const history = createBrowserHistory();
  history.listen((location) => {
    analytics.trackPageChange(location);
  });

  return (
    <>
      <Router history={history}>
        <Switch>
          <Route path={['/sandbox/', '/chart-maker/sandbox/']}>
            <Navbar />
            <div className="page-container">
              <Suspense fallback={<LoadingPage text="Loading sandbox..." />}>
                <Sandbox onSwitchToChartBuilder={handleSwitchToChartBuilder} />
              </Suspense>
            </div>
          </Route>
          <Route path={['/edit/:id', '/chart-maker/edit/:id']}>
            <Navbar />
            <div className="page-container">
              <LoadBuilder />
            </div>
          </Route>
          <Route path={['/view/:id', '/chart-maker/view/:id']}>
            <div className="page-container">
              <LoadBuilder viewOnly />
            </div>
          </Route>
          <Route path="/">
            <Navbar />
            <div className="page-container">
              <Suspense
                fallback={
                  <LoadingPage
                    text={
                      sandboxConfig === null
                        ? 'Loading chart maker...'
                        : 'Loading chart maker with your config...'
                    }
                  />
                }
              >
                <Builder baseConfig={sandboxConfig} />
              </Suspense>
            </div>
          </Route>
        </Switch>
      </Router>
    </>
  );
};

export default Entrypoint;
